import { useEffect, useMemo, useState } from "react";
import { DataTable, Modal2 } from "@iq/models";
import useIsSmallScreen from "../../hooks/useIsSmallScreen";
import { getAppointmentHistory, fetchAppointmentReceipt } from "../../services/customerService";

function CustomerDetails({
    isOpen,
    customer,
    onClose,
}) {
    const isSmallScreen = useIsSmallScreen();
    const [appointmentHistory, setAppointmentHistory] = useState([]);
    const [questionnaireAnswers, setQuestionnaireAnswers] = useState([]);
    const [activeView, setActiveView] = useState("history");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (customer) {
            fetchHistory(customer.id);
        }
    }, [customer]);

    const tableConfig = useMemo(() => ({
        columns: [
            {
                key: "id",
                label: "Appointment",
                accessor: "id",
                width: "1fr",
                formatter: (id) => `#${id}`,
                filterable: true,
            },
            {
                key: "appointment_date",
                label: "Date",
                accessor: "appointment_date",
                width: "3fr",
                formatter: formatDate,
                filterable: true,
            },
            {
                key: "payment_status",
                label: "Payment",
                accessor: "payment_status",
                width: "3fr",
                filterable: true,
            },
            {
                key: "payment_method",
                label: "Method",
                accessor: "payment_method",
                width: "2fr",
                formatter: formatPaymentMethod,
                filterable: true,
                quickValues: ["Cash", "Card"],
            },
            {
                key: "actions",
                label: "Actions",
                accessor: "id",
                width: "5fr",
                renderCell: ({ row }) => (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                openQuestionnaire();
                            }}
                            style={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                border: "1px solid #0f766e",
                                background: "#ecfeff",
                                color: "#115e59",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            View Q&A
                        </button>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                openReceipt(row.id);
                            }}
                            style={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                border: "1px solid #2d6cdf",
                                background: "#eaf1ff",
                                color: "#1d4ed8",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Download PDF
                        </button>
                    </div>
                ),
                filterable: false,
                searchable: false,
            },
        ],
        emptyLabel: "No paid appointments found.",
        searchPlaceholder: "Search appointments",
    }), [customer]);

    const mobileTableConfig = useMemo(() => ({
        columns: [
            {
                key: "id",
                label: "Appointment",
                accessor: "id",
                width: "1.5fr",
                formatter: (id) => `#${id}`,
            },
            {
                key: "appointment_date",
                label: "Date",
                accessor: "appointment_date",
                width: "3fr",
                formatter: formatDate,
                filterable: true,
            },
            {
                key: "payment_status",
                label: "Payment",
                accessor: "payment_status",
                width: "3fr",
            },
            {
                key: "payment_method",
                label: "Method",
                accessor: "payment_method",
                width: "2fr",
                formatter: formatPaymentMethod,
            },
            {
                key: "actions",
                label: "Actions",
                accessor: "id",
                width: "4fr",
                renderCell: ({ row }) => (
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", width: "100%", flexWrap: "wrap" }}>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                openQuestionnaire();
                            }}
                            style={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                border: "1px solid #0f766e",
                                background: "#ecfeff",
                                color: "#115e59",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            View Q&A
                        </button>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                openReceipt(row.id);
                            }}
                            style={{
                                padding: "6px 10px",
                                borderRadius: "6px",
                                border: "1px solid #2d6cdf",
                                background: "#eaf1ff",
                                color: "#1d4ed8",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Download PDF
                        </button>
                    </div>
                ),
                filterable: false,
                searchable: false,
            },
        ],
        emptyLabel: "No paid appointments found.",
        searchPlaceholder: "Search appointments",
    }), [customer]);

    async function fetchHistory(customerId) {
        setIsLoading(true);
        setErrorMessage("");

        try {
            const customerWithHistory = await getAppointmentHistory(customerId);
            const history = Array.isArray(customerWithHistory?.appointments)
                ? customerWithHistory.appointments
                : [];

            setAppointmentHistory(history);
        } catch (error) {
            setErrorMessage(error.message || "Failed to load appointment history");
            setAppointmentHistory([]);
        } finally {
            setIsLoading(false);
        }
    }

    function openQuestionnaire() {
        const answers = Array.isArray(customer?.questionnaire_answers)
            ? customer.questionnaire_answers
            : [];

        setQuestionnaireAnswers(answers);
        setActiveView("questions");
    }

    function handleModalClose() {
        setActiveView("history");
        setQuestionnaireAnswers([]);
        setErrorMessage("");
        onClose();
    }

    async function openReceipt(appointmentId) {
        try {
            setErrorMessage("");
            const pdf = await fetchAppointmentReceipt(appointmentId);
            const url = window.URL.createObjectURL(pdf);
            const link = document.createElement("a");
            link.href = url;
            link.download = `appointment-receipt-${appointmentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch (error) {
            setErrorMessage(error.message || "Failed to load receipt");
        }
    }

    return (
        <Modal2
            isOpen={isOpen}
            onClose={handleModalClose}
            title={
                activeView === "questions"
                    ? (customer ? `${customer.name}'s Questionnaire Answers` : "Questionnaire Answers")
                    : (customer ? `${customer.name}'s Appointment History` : "Customer Details")
            }
            styles={{ panel: { width: "min(90rem, 100%)", maxHeight: "90dvh" } }}
        >
            {errorMessage && <p className="error">{errorMessage}</p>}

            {activeView === "questions" ? (
                questionnaireAnswers.length === 0 ? (
                    <div>
                        <p>No questionnaire answers saved for this customer.</p>
                        <button
                            type="button"
                            onClick={() => setActiveView("history")}
                            style={{
                                marginTop: 8,
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                background: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            Back to appointment history
                        </button>
                    </div>
                ) : (
                    <div>
                        <button
                            type="button"
                            onClick={() => setActiveView("history")}
                            style={{
                                marginBottom: 12,
                                padding: "8px 12px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                background: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            Back to appointment history
                        </button>
                        <div style={{ display: "grid", gap: "12px" }}>
                            {questionnaireAnswers.map((entry, index) => (
                                <div
                                    key={`${entry.question_id ?? index}-${entry.question ?? index}`}
                                    style={{
                                        border: "1px solid #dbe4f0",
                                        borderRadius: "10px",
                                        padding: "12px 14px",
                                        background: "#f8fafc",
                                    }}
                                >
                                    <p style={{ margin: "0 0 6px", fontWeight: 600 }}>
                                        {entry.question || "Question"}
                                    </p>
                                    <p style={{ margin: 0, color: "#1f2937" }}>
                                        {entry.answer || "No answer provided"}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            ) : isLoading ? (
                <p>Loading appointment history...</p>
            ) : (
                <div>
                    <DataTable
                        rowData={appointmentHistory}
                        rowKey={(appointment) => appointment.id}
                        tableConfig={isSmallScreen ? mobileTableConfig : tableConfig}
                    />
                </div>
            )}
        </Modal2>
    );
}

function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
}

function formatPaymentMethod(value) {
    return value || "-";
}

export default CustomerDetails;